import { useCallback, useContext, useState, type MouseEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ListIcon,
  Layers,
  LogOutIcon,
  Menu,
  MessageCircleIcon,
  Moon,
  Sun,
  X,
  Blocks,
} from "lucide-react";
import { ThemeContext } from "../utils/ThemeContext";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../utils/LanguageSwitcher";
import { appRoutes } from "../../../router";
import { useTrainingStore } from "../../../core/store/trainingStore";
import { useTrainingRoute } from "../../../core/hooks/useTrainingRoute";
import Logger from "../../../utils/Logger";
import { Theme } from "../../../theme";
import { EditModeContext } from "../utils/EditModeContext";
import { useUnsavedChanges } from "../utils/UnsavedChangesContext";
import { useAdminPermissions } from "../../../core/hooks/useAdminPermissions";
import Toggle from "../utils/Toggle";
import { Environement } from "../../../utils/Environement";
import { pb } from "../../../providers/pocketbase/pocketbase";

export default function Sidebar() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { darkMode, setDarkMode } = useContext(ThemeContext);
  const { editMode, toggleEditMode } = useContext(EditModeContext);
  const permissions = useAdminPermissions();
  const { requestNavigation } = useUnsavedChanges();
  const { trainings } = useTrainingStore();
  const {
    trainingId,
    trainingPath,
    categoryPath,
    isCategoryRoute,
    testimonyPath,
    isTopicRoute,
    isTestimonyRoute,
    trainingVersionsPath,
    isTrainingVersionsRoute,
  } = useTrainingRoute();

  const handleLogout = useCallback(async () => {
    try {
      await pb.authStore.clear();
      requestNavigation(() => navigate(appRoutes.signin));
    } catch (error) {
      Logger.error("Error signing out:", error);
    }
  }, [navigate, requestNavigation]);

  const handleNavClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, to: string) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      requestNavigation(() => navigate(to));
    },
    [navigate, requestNavigation]
  );

  return (
    <>
      {/* Black background when menu is open */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      {/* Bouton burger visible sur mobile */}
      <button
        className="md:hidden p-2 text-gray-800 bg-blue-50 dark:bg-gray-700 dark:text-white rounded-3xl fixed top-4 right-4 z-50"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Menu size={24} />
      </button>

      {/* Sidebar */}
      <div
        className={`flex flex-col fixed top-0 bottom-0 left-0 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out z-50 select-none 
          ${
            isOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0 md:relative md:w-[250px]`}
      >
        <div className="p-4 flex justify-between items-center">
          <span className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("menu.title")}
          </span>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-800 dark:text-white md:hidden"
          >
            <X size={24} />
          </button>
        </div>
        <hr className="border-gray-200 dark:border-gray-700" />
        <nav className="py-4 px-2 space-y-4 flex-1">
          {(trainings ?? []).map((training, idx) => (
            <div key={idx}>
              <label className="block ps-2 text-[12px] font-semibold text-gray-400 dark:text-gray-400">
                {training.name}
              </label>
              <Link
                key={idx}
                to={trainingPath(training.id)}
                onClick={(event) => handleNavClick(event, trainingPath(training.id))}
                className={Theme.link(isTopicRoute)}
                title={training.description}
              >
                <ListIcon size={16} />
                {t("menu.topics")}
              </Link>
              <>
                <Link
                  to={trainingVersionsPath(trainingId)}
                  onClick={(event) =>
                    handleNavClick(event, trainingVersionsPath(trainingId))
                  }
                  className={Theme.link(isTrainingVersionsRoute)}
                  title="training-versions"
                >
                  <Layers size={16} />
                  {t("menu.trainingVersions")}
                </Link>
                <Link
                  to={categoryPath(trainingId)}
                  onClick={(event) => handleNavClick(event, categoryPath(trainingId))}
                  className={Theme.link(isCategoryRoute)}
                  title="category"
                >
                  <Blocks size={16} />
                  {t("menu.categories")}
                </Link>
                <Link
                  to={testimonyPath(trainingId)}
                  onClick={(event) => handleNavClick(event, testimonyPath(trainingId))}
                  className={Theme.link(isTestimonyRoute)}
                  title="image"
                >
                  <MessageCircleIcon size={16} />
                  {t("menu.testimonies")}
                </Link>
                <div className="h-5" />
              </>
            </div>
          ))}
        </nav>
        <div className="px-2 py-4">
          {permissions.canEdit && (
            <button className={Theme.link(false)}>
              <Toggle
                enabled={editMode}
                onChange={toggleEditMode}
                label={t("menu.editMode")}
              />
            </button>
          )}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={Theme.link(false)}
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            <span className="ms-3">{t("menu.darkMode")}</span>
          </button>
        </div>
        <hr className="border-gray-200 dark:border-gray-700" />
        <div className="px-2 py-4">
          <button onClick={handleLogout} className={Theme.link(false)}>
            <LogOutIcon size={20} />
            {t("menu.signOut")}
          </button>
        </div>
        {Environement.envIsDev && <LanguageSwitcher />}
      </div>
    </>
  );
}
